import { Field, Item, LinkField, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import React from 'react';

import ResponsiveImages from 'components/helpers/ResponsiveImages/ResponsiveImages';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { getRenderingParam } from 'lib/rendering-params';
import type { RecordOfFields } from 'models/fields';
import type { ResponsiveImageFields } from 'models/responsive-image';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { ButtonVariant } from 'components/helpers/Button/Button';
import { IconVariant } from 'components/helpers/Icon';
import Link, { LinkVariant } from 'components/helpers/Link/Link';

import styles from './PageBannerWithOverlay.module.scss';

type PageBannerWithOverlayFields = ResponsiveImageFields & {
  bannerBody: Field<string>;
  bannerBodyExpanded: string;
  bannerLinkDestination: LinkField;
  bannerLinkIcon: Item | null;
  bannerLinkMode: Item | null;
  bannerLinkText: Field<string>;
  bannerLinkTextExpanded: string;
  bannerSubtitle: Field<string>;
  bannerSubtitleExpanded: string;
  bannerTitle: Field<string>;
  bannerTitleExpanded: string;
  bannerTitleHeadingLevel: Item | null;
  bannerTitleTextStyle: Item | null;
  bannerSubtitleHeadingLevel: Item | null;
  bannerSubtitleTextStyle: Item | null;
};

type PageBannerWithOverlayProps = ComponentProps & {
  fields: PageBannerWithOverlayFields;
};

const cx = classNames.bind(styles);

const PageBannerWithOverlay = (props: PageBannerWithOverlayProps): React.JSX.Element => {
  const { fields, className, params } = props;
  const { sitecoreContext } = useSitecoreContext();

  const overlayMode = getRenderingParam(params.overlayMode);

  const linkIconField = fields.bannerLinkIcon?.fields as RecordOfFields;
  const linkIconValue = getFieldValue<IconVariant>(linkIconField, 'Value');

  const linkModeField = fields.bannerLinkMode?.fields as RecordOfFields;
  const linkModeValue = getFieldValue<ButtonVariant | LinkVariant>(linkModeField, 'Value');

  const bannerTitleHeadingLevelItemField = fields.bannerTitleHeadingLevel?.fields as RecordOfFields;
  const bannerTitleHeadingLevelValue = getFieldValue<string>(bannerTitleHeadingLevelItemField, 'Value') || 'h1';

  const bannerSubtitleHeadingLevelItemField = fields.bannerSubtitleHeadingLevel?.fields as RecordOfFields;
  const bannerSubtitleHeadingLevelValue = getFieldValue<string>(bannerSubtitleHeadingLevelItemField, 'Value') || 'h1';

  const bannerTitleTextStyleItemField = fields.bannerTitleTextStyle?.fields as RecordOfFields;
  const bannerTitleTextStyleValue = getFieldValue<string>(bannerTitleTextStyleItemField, 'Value') || 'font-style-xl';

  const bannerSubtitleTextStyleItemField = fields.bannerSubtitleTextStyle?.fields as RecordOfFields;
  const bannerSubtitleTextStyleValue =
    getFieldValue<string>(bannerSubtitleTextStyleItemField, 'Value') || 'font-style-xl';

  const imageClasses: string = cx('image');
  const textClasses: string = cx('text', 'bg-purple-60', overlayMode);

  return (
    <div className={cx({ banner: true }, 'component-container-edge', className)}>
      <div className={imageClasses} key="page-banner-overlay-image">
        <ResponsiveImages
          desktopImage={fields.desktopImage}
          mobileImage={fields.mobileImage}
          tabletImage={fields.tabletImage}
          imageType={fields.imageType}
          imageAreaBackgroundColor={fields.imageAreaBackgroundColor}
        />
      </div>

      <div className={textClasses} key="page-banner-overlay-text">
        <Text
          className={cx({ title: true, [bannerTitleTextStyleValue]: true })}
          encode={sitecoreContext.pageEditing}
          field={sitecoreContext.pageEditing ? fields.bannerTitle : { value: fields.bannerTitleExpanded }}
          tag={bannerTitleHeadingLevelValue}
          editable={true}
        />
        <Text
          className={cx({ subtitle: true, [bannerSubtitleTextStyleValue]: true })}
          field={sitecoreContext.pageEditing ? fields.bannerSubtitle : { value: fields.bannerSubtitleExpanded }}
          tag={bannerSubtitleHeadingLevelValue}
          encode={sitecoreContext.pageEditing}
        />
        <RichTextWrapper
          className={cx('body', 'bg-inherit-dark', 'text-white')}
          field={sitecoreContext.pageEditing ? fields.bannerBody : { value: fields.bannerBodyExpanded }}
          tag="div"
        />
        {props.fields.bannerLinkDestination?.value.href !== '' && (
          <Link
            className={cx('link')}
            field={fields.bannerLinkDestination}
            icon={linkIconValue}
            showLinkTextWithChildrenPresent={false}
            variant={linkModeValue}
          >
            <Text
              encode={sitecoreContext.pageEditing}
              field={sitecoreContext.pageEditing ? fields.bannerLinkText : { value: fields.bannerLinkTextExpanded }}
            />
          </Link>
        )}
      </div>
    </div>
  );
};

export default compose<PageBannerWithOverlayProps>(withValidDatasourceCheck(), withStyles())(PageBannerWithOverlay);
